import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Body, Heading } from 'components'

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const AssortmentContent = styled.div`
  label: AssortmentContent;
  flex: 1 1 auto;
  overflow-y: scroll;
  background-color: ${(props) => props.theme.bgColors.primary};

  &::-webkit-scrollbar {
    display: none;
  }
`

export const AssortmentHeader = styled.div`
  label: AssortmentHeader;
  padding: 2rem ${(props) => props.theme.item.desktop.padding} 0;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 2rem ${(props) => props.theme.item.mobile.padding} 0;
  }
`

export const AssortmentTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
`

export const AssortmentTitle = styled(Heading)`
  display: block;
`

export const AssortmentPrice = styled.div`
  flex: 0 0 auto;
  text-align: right;
`

export const AssortmentProgress = styled(Body)`
  display: block;
  margin: 0.4rem 0 0;
  color: ${(props) => props.theme.modifiers.text.color};
`

export const AssortmentDescription = styled(Body)`
  display: block;
  margin: 1rem 0 0;
  white-space: pre-line;
`

export const AssortmentSlotList = styled.ul`
  label: AssortmentSlotList;
  list-style: none;
  margin: 0;
  padding: 2rem ${(props) => props.theme.item.desktop.padding};
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 2rem ${(props) => props.theme.item.mobile.padding};
  }
`

export const AssortmentSlot = styled.li<{
  filled: boolean
  attention: boolean
}>`
  label: AssortmentSlot;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  border-style: solid;
  border-width: 0.1rem;
  border-radius: ${(props) => props.theme.cards.modifier.borderRadius};
  background-color: ${(props) => props.theme.modifiers.colors.bgColor};
  border-color: ${(props) =>
    props.attention
      ? props.theme.colors.error
      : props.filled
      ? props.theme.modifiers.selectedColors.borderColor
      : `color-mix(in srgb, ${props.theme.modifiers.colors.borderColor} 30%, transparent)`};
`

export const AssortmentSlotMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 1.2rem;
`

export const AssortmentSlotMarker = styled.span<{ filled: boolean }>`
  flex: 0 0 3.2rem;
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  background-color: ${(props) =>
    props.filled ? props.theme.success.bgColor : props.theme.bgColors.tertiary};
  color: ${(props) =>
    props.filled
      ? props.theme.success.color
      : props.theme.modifiers.text.color};
`

export const AssortmentSlotText = styled.div`
  min-width: 0;
`

export const AssortmentSlotLabel = styled(Body)`
  display: block;
  font-size: ${(props) => props.theme.fonts.sizes.xSmall};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(props) => props.theme.modifiers.text.color};
`

export const AssortmentSlotName = styled(Heading)<{ muted?: boolean }>`
  display: block;
  ${(props) => (props.muted ? 'opacity: 0.6;' : '')}
`

export const AssortmentSlotSummary = styled(Body)`
  display: block;
  margin: 0.3rem 0 0;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  color: ${(props) => props.theme.modifiers.text.color};
`

export const AssortmentSlotWarning = styled(Body)`
  display: block;
  margin: 0.3rem 0 0;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  color: ${(props) => props.theme.colors.error};
`

export const AssortmentSlotActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.8rem;

  button {
    width: 100%;
    min-height: 4rem;
    white-space: nowrap;
  }
`

export const AssortmentQuantity = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${(props) => props.theme.item.desktop.padding} 2rem;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 0 ${(props) => props.theme.item.mobile.padding} 2rem;
  }
`

export const AssortmentQuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;

  button {
    width: 4rem;
    height: 4rem;
    padding: 0;
    border-radius: 2rem;
  }
`

export const AssortmentFooter = styled.div`
  label: AssortmentFooter;
  flex: 0 0 auto;
  position: relative;
  padding: 2rem ${(props) => props.theme.item.desktop.padding};
  background-color: ${(props) => props.theme.bgColors.primary};
  box-shadow: 0 -15px 30px ${(props) => props.theme.overlay.primary};
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding: 2rem ${(props) => props.theme.item.mobile.padding};
  }
`

export const AssortmentFooterButtons = styled.div`
  margin: 0 -0.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const AssortmentFooterButton = styled.div<{ grow?: number }>`
  flex: ${(props) => props.grow ?? 1} 1 0;
  padding: 0 0.6rem;

  button {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
    white-space: nowrap;
  }
`

export const AssortmentOverlay = styled.div`
  position: absolute;
  z-index: 15;
  inset: 0;
  background-color: ${(props) => props.theme.overlay.dark};
  animation: ${fadeIn} 0.2s ease-out;
`

export const AssortmentSheetView = styled.div`
  position: absolute;
  z-index: 16;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 80%;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.bgColors.primary};
  box-shadow: 0 -15px 30px ${(props) => props.theme.overlay.primary};
  animation: ${slideUp} 0.25s ease-out;
  outline: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const AssortmentSheetHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.6rem ${(props) => props.theme.item.desktop.padding};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 1.6rem ${(props) => props.theme.item.mobile.padding};
  }
`

export const AssortmentSheetList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 ${(props) => props.theme.item.desktop.padding} 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 0 ${(props) => props.theme.item.mobile.padding} 2rem;
  }
`

export const AssortmentChoice = styled.button<{ selected: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  text-align: left;
  border-style: solid;
  border-width: 0.1rem;
  border-radius: ${(props) => props.theme.cards.modifier.borderRadius};
  background-color: ${(props) => props.theme.modifiers.colors.bgColor};
  border-color: ${(props) =>
    props.selected
      ? props.theme.modifiers.selectedColors.borderColor
      : props.theme.modifiers.colors.borderColor};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const AssortmentChoiceText = styled.span`
  display: block;
  min-width: 0;
`

export const AssortmentChoiceMeta = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
`

export const AssortmentCustomizeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 2rem ${(props) => props.theme.item.desktop.padding} 1.6rem;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 2rem ${(props) => props.theme.item.mobile.padding} 1.6rem;
  }
`

export const AssortmentBack = styled.button`
  flex: 0 0 auto;
  width: 4rem;
  height: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2.4rem;
  line-height: 1;
  color: ${(props) => props.theme.links.dark.color};

  &:hover {
    color: ${(props) => props.theme.links.dark.hover};
  }
`

export const AssortmentCustomizeBody = styled.div`
  padding: 0 ${(props) => props.theme.item.desktop.padding};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 0 ${(props) => props.theme.item.mobile.padding};
  }
`

export const AssortmentIngredients = styled.div`
  padding: 2rem ${(props) => props.theme.item.desktop.padding};
  margin: 0 0 ${(props) => props.theme.item.desktop.padding};
  background-color: ${(props) => props.theme.bgColors.tertiary};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: 2rem ${(props) => props.theme.item.mobile.padding};
  }
`

export const AssortmentIngredientTitle = styled(Heading)<{
  invalid?: boolean
}>`
  display: block;
  margin: 0 0 1rem;
  ${(props) => (props.invalid ? `color: ${props.theme.colors.error};` : '')}
`

export const AssortmentIngredientActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 1.2rem;
`

export const AssortmentTextButton = styled.button`
  padding: 0.6rem 0.2rem;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  text-decoration: underline;
  color: ${(props) => props.theme.links.dark.color};

  &:hover:not(:disabled) {
    color: ${(props) => props.theme.links.dark.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    text-decoration: none;
  }
`

export const AssortmentIngredientRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 0;
  border-bottom: 0.1rem solid
    ${(props) => props.theme.modifiers.colors.borderColor};
`

export const AssortmentSegmented = styled.div`
  flex: 0 0 auto;
  display: flex;
`

export const AssortmentSegment = styled.button<{ selected: boolean }>`
  min-width: 8rem;
  padding: 0.8rem 1.4rem;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  border-style: solid;
  border-width: 0.1rem;
  border-color: ${(props) => props.theme.modifiers.selectedColors.borderColor};
  background-color: ${(props) =>
    props.selected
      ? props.theme.modifiers.selectedColors.bgColor
      : props.theme.modifiers.colors.bgColor};
  color: ${(props) =>
    props.selected
      ? props.theme.modifiers.title.selectedColor
      : props.theme.modifiers.title.color};

  &:first-of-type {
    border-radius: 2rem 0 0 2rem;
  }

  &:last-of-type {
    border-radius: 0 2rem 2rem 0;
    border-left-width: 0;
  }
`
